/**
 * SectionPropertiesPanel — extracted from AdvancedFormBuilder.js
 * Archive: /formbuilder/archive/AdvancedFormBuilder.v1.full.js (3232 lines)
 *
 * Renders the "Section Properties" inspector pane for the selected section.
 * All mutations are handled via callbacks passed from AdvancedFormBuilder.
 */
import React from 'react';

export function SectionPropertiesPanel({
  currentSection,
  onUpdateSection,
  onUpdateSectionStyle,
  onUpdateSectionTitleStyle,
  onSectionBackgroundUpload,
}) {
  const updateSection = (id, updates) => onUpdateSection(id, updates);
  const updateSectionStyle = (id, styleUpdates) => onUpdateSectionStyle(id, styleUpdates);
  const updateSectionTitleStyle = (id, styleUpdates) => onUpdateSectionTitleStyle(id, styleUpdates);
  const handleSectionBackgroundUpload = (event, id) => onSectionBackgroundUpload(event, id);

  if (!currentSection) return null;

  return (
    <div className="properties-group">
      <h4>Section Properties</h4>

      {/* Basic Section Settings */}
      <div className="property-section">
        <h5>📋 Basic Settings</h5>
        <div className="property-group">
          <label>Section Title:</label>
          <input
            type="text"
            value={currentSection.title}
            onChange={(e) =>
              updateSection(currentSection.id, { title: e.target.value })
            }
          />
        </div>

        <div className="property-group">
          <label>Columns Layout:</label>
          <select
            value={currentSection.columns}
            onChange={(e) =>
              updateSection(currentSection.id, { columns: parseInt(e.target.value) })
            }
          >
            <option value={1}>1 Column</option>
            <option value={2}>2 Columns</option>
            <option value={3}>3 Columns</option>
          </select>
        </div>

        {/* Section Positioning */}
        <div className="property-group">
          <label>Section Position:</label>
          <div className="alignment-controls">
            <button
              className={`align-btn ${currentSection.styling?.alignSelf === 'flex-start' || !currentSection.styling?.alignSelf ? 'active' : ''}`}
              onClick={() => updateSectionStyle(currentSection.id, {
                alignSelf: 'flex-start',
                marginLeft: '0',
                marginRight: 'auto'
              })}
              title="Align Left"
            >
              ⬅️ Left
            </button>
            <button
              className={`align-btn ${currentSection.styling?.alignSelf === 'center' ? 'active' : ''}`}
              onClick={() => updateSectionStyle(currentSection.id, {
                alignSelf: 'center',
                marginLeft: 'auto',
                marginRight: 'auto'
              })}
              title="Align Center"
            >
              ⬜ Center
            </button>
            <button
              className={`align-btn ${currentSection.styling?.alignSelf === 'flex-end' ? 'active' : ''}`}
              onClick={() => updateSectionStyle(currentSection.id, {
                alignSelf: 'flex-end',
                marginLeft: 'auto',
                marginRight: '0'
              })}
              title="Align Right"
            >
              ➡️ Right
            </button>
          </div>
        </div>
      </div>

      {/* Title Styling */}
      <div className="property-section">
        <h5>🏷️ Title Styling</h5>
        <div className="property-group">
          <label>Title Alignment:</label>
          <div className="alignment-controls">
            {['left', 'center', 'right'].map((align) => (
              <button
                key={align}
                className={`align-btn ${currentSection.titleStyling?.textAlign === align ? 'active' : ''}`}
                onClick={() =>
                  updateSectionTitleStyle(currentSection.id, { textAlign: align })
                }
                title={`Align ${align}`}
              >
                {align === 'left' ? '⬅️' : align === 'center' ? '⬜' : '➡️'}
              </button>
            ))}
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Title Color:</label>
            <input
              type="color"
              value={currentSection.titleStyling?.color || '#2d3748'}
              onChange={(e) =>
                updateSectionTitleStyle(currentSection.id, { color: e.target.value })
              }
            />
          </div>
          <div className="property-group">
            <label>Font Size:</label>
            <input
              type="text"
              value={currentSection.titleStyling?.fontSize || '20px'}
              onChange={(e) =>
                updateSectionTitleStyle(currentSection.id, { fontSize: e.target.value })
              }
              placeholder="20px"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Font Weight:</label>
            <select
              value={currentSection.titleStyling?.fontWeight || 'bold'}
              onChange={(e) =>
                updateSectionTitleStyle(currentSection.id, { fontWeight: e.target.value })
              }
            >
              <option value="normal">Normal</option>
              <option value="bold">Bold</option>
              <option value="600">Semi Bold</option>
              <option value="700">Bold</option>
              <option value="800">Extra Bold</option>
            </select>
          </div>
          <div className="property-group">
            <label>Margin Bottom:</label>
            <input
              type="text"
              value={currentSection.titleStyling?.marginBottom || '15px'}
              onChange={(e) =>
                updateSectionTitleStyle(currentSection.id, {
                  marginBottom: e.target.value,
                })
              }
              placeholder="15px"
            />
          </div>
        </div>
      </div>

      {/* Section Background */}
      <div className="property-section">
        <h5>🎨 Section Background</h5>
        <div className="property-group">
          <label>Background Color:</label>
          <div className="background-color-controls">
            <input
              type="color"
              value={currentSection.styling?.backgroundColor || '#ffffff'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { backgroundColor: e.target.value })
              }
            />
            <button
              className="no-color-btn"
              onClick={() =>
                updateSectionStyle(currentSection.id, { backgroundColor: 'transparent' })
              }
              title="No Color"
            >
              🚫 No Color
            </button>
          </div>
        </div>

        <div className="property-group">
          <label>Background Image:</label>
          <div className="background-image-controls">
            <div className="background-upload-controls">
              <button
                className="upload-background-btn"
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = 'image/*';
                  input.onchange = (e) =>
                    handleSectionBackgroundUpload(e, currentSection.id);
                  input.click();
                }}
              >
                📁 Upload Background
              </button>
              {currentSection.styling?.backgroundImage && (
                <button
                  className="remove-background-btn"
                  onClick={() =>
                    removeSectionBackgroundImage(currentSection.id)
                  }
                >
                  🗑️ Remove
                </button>
              )}
            </div>
            {currentSection.styling?.backgroundImage && (
              <div className="background-preview">
                <div
                  className="preview-image"
                  style={{
                    backgroundImage: currentSection.styling.backgroundImage,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    height: '80px',
                    borderRadius: '4px',
                    marginTop: '8px',
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {currentSection.styling?.backgroundImage && (
          <div className="property-row">
            <div className="property-group">
              <label>Background Size:</label>
              <select
                value={currentSection.styling.backgroundSize || 'cover'}
                onChange={(e) =>
                  updateSectionStyle(currentSection.id, {
                    backgroundSize: e.target.value,
                  })
                }
              >
                <option value="cover">Cover</option>
                <option value="contain">Contain</option>
                <option value="auto">Auto</option>
                <option value="100% 100%">100% 100%</option>
              </select>
            </div>
            <div className="property-group">
              <label>Background Position:</label>
              <select
                value={currentSection.styling.backgroundPosition || 'center'}
                onChange={(e) =>
                  updateSectionStyle(currentSection.id, {
                    backgroundPosition: e.target.value,
                  })
                }
              >
                <option value="center">Center</option>
                <option value="top">Top</option>
                <option value="bottom">Bottom</option>
                <option value="left">Left</option>
                <option value="right">Right</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Section Layout */}
      <div className="property-section">
        <h5>📐 Layout & Spacing</h5>

        <div className="property-row">
          <div className="property-group">
            <label>Width:</label>
            <input
              type="text"
              value={currentSection.styling?.width || '100%'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { width: e.target.value })
              }
              placeholder="e.g. 100% or 800px"
            />
          </div>
          <div className="property-group">
            <label>Height:</label>
            <input
              type="text"
              value={currentSection.styling?.height || 'auto'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { height: e.target.value })
              }
              placeholder="e.g. auto or 400px"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Max Width:</label>
            <input
              type="text"
              value={currentSection.styling?.maxWidth || '100%'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { maxWidth: e.target.value })
              }
              placeholder="e.g. 1200px"
            />
          </div>
          <div className="property-group">
            <label>Min Width:</label>
            <input
              type="text"
              value={currentSection.styling?.minWidth || 'auto'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { minWidth: e.target.value })
              }
              placeholder="e.g. 300px"
            />
          </div>
          <div className="property-group">
            <label>Max Height:</label>
            <input
              type="text"
              value={currentSection.styling?.maxHeight || 'auto'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { maxHeight: e.target.value })
              }
              placeholder="e.g. 600px"
            />
          </div>
          <div className="property-group">
            <label>Min Height:</label>
            <input
              type="text"
              value={currentSection.styling?.minHeight || 'auto'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { minHeight: e.target.value })
              }
              placeholder="e.g. 300px"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Padding:</label>
            <input
              type="text"
              value={currentSection.styling?.padding || '30px'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { padding: e.target.value })
              }
              placeholder="30px"
            />
          </div>
          <div className="property-group">
            <label>Margin:</label>
            <input
              type="text"
              value={currentSection.styling?.margin || '20px 0'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { margin: e.target.value })
              }
              placeholder="20px 0"
            />
          </div>
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Border Radius:</label>
            <input
              type="text"
              value={currentSection.styling?.borderRadius || '12px'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { borderRadius: e.target.value })
              }
              placeholder="12px"
            />
          </div>
          <div className="property-group">
            <label>Border:</label>
            <input
              type="text"
              value={currentSection.styling?.border || '1px solid #e2e8f0'}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { border: e.target.value })
              }
              placeholder="1px solid #e2e8f0"
            />
          </div>
        </div>
      </div>

      {/* Section Effects */}
      <div className="property-section">
        <h5>✨ Effects & Shadows</h5>
        <div className="property-group">
          <label>Box Shadow:</label>
          <input
            type="text"
            value={currentSection.styling?.boxShadow || '0 2px 10px rgba(0,0,0,0.1)'}
            onChange={(e) =>
              updateSectionStyle(currentSection.id, { boxShadow: e.target.value })
            }
            placeholder="0 2px 10px rgba(0,0,0,0.1)"
          />
        </div>

        <div className="property-row">
          <div className="property-group">
            <label>Opacity:</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={currentSection.styling?.opacity || 1}
              onChange={(e) =>
                updateSectionStyle(currentSection.id, { opacity: e.target.value })
              }
            />
            <span className="range-value">
              {(currentSection.styling?.opacity || 1) * 100}%
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SectionPropertiesPanel;
